import { RotateCcw } from "lucide-react";
import { useMistSequence, STAGES } from "@/hooks/useMistSequence";
import { MistDefs, HouseOutline, MistBlobs, ScanPlane, Markers } from "./MistParts";

const STAGE_LABEL = { unmeasured: "Unmeasured", measured: "Measured", explained: "Explained" };
const MIST_OPACITY = [0.92, 0.38, 0.18];

const FINDINGS = [
  { k: "Bedroom CO₂ rises overnight", v: "Improve night-time ventilation" },
  { k: "Lead detected at tap, below action level", v: "Discuss flushing habits and filtration" },
  { k: "NO₂ peaks while cooking", v: "Run the hood whenever burners are on" },
];

const StageBar = ({ index, onReplay }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
    <ol className="flex flex-wrap items-center gap-1.5 sm:gap-2" aria-label="Stages" data-testid="mist-stages">
      {STAGES.map((s, idx) => (
        <li key={s} className={`mono uppercase tracking-[0.14em] px-2 py-1 rounded transition-colors ${idx === index ? "bg-forest text-white" : idx < index ? "text-ink" : "text-sec"}`} aria-current={idx === index ? "step" : undefined}>
          {STAGE_LABEL[s]}
        </li>
      ))}
    </ol>
    <button type="button" onClick={onReplay} className="inline-flex items-center gap-1.5 text-xs font-medium text-sec hover:text-ink min-h-[44px] sm:min-h-[36px] px-2" data-testid="mist-replay">
      <RotateCcw size={13} /> Replay
    </button>
  </div>
);

const Findings = ({ visible }) => (
  <div className={`mt-3 transition-opacity duration-500 min-h-[108px] sm:min-h-[96px] ${visible ? "opacity-100" : "opacity-0"}`} data-testid="mist-findings" aria-hidden={!visible}>
    <ul className="rounded-md border border-divider bg-white grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-divider">
      {FINDINGS.map((f) => (
        <li key={f.k} className="flex flex-col gap-0.5 px-3 py-2.5 text-[12px] sm:text-[13px]">
          <span className="text-ink">{f.k}</span>
          <span className="font-medium text-ink">→ {f.v}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const Mist = () => {
  const { stageIndex: i, scan, play } = useMistSequence();
  return (
    <figure className="relative" data-testid="mist-visual" aria-label="Illustrative visualization: mist over a home outline resolving into measurement markers and findings">
      <StageBar index={i} onReplay={play} />
      <div className="relative aspect-[4/3] max-h-[440px] w-full overflow-hidden rounded-md border border-divider bg-white">
        <svg viewBox="0 0 440 400" className="absolute inset-0 h-full w-full" aria-hidden="true">
          <MistDefs />
          <HouseOutline dim={i === 0} />
          <MistBlobs opacity={MIST_OPACITY[i]} />
          <ScanPlane active={scan} />
          <Markers visible={i >= 1} />
        </svg>
      </div>
      <Findings visible={i === 2} />
      <figcaption className="mt-2 mono text-sec" data-testid="mist-caption">Illustrative visualization · not live readings</figcaption>
    </figure>
  );
};
